import { twMerge } from 'tailwind-merge';

import { Alert, AlertBannerUse, LinkUse } from '@growtherapy/sprout-ui';

import { MarkdownLinkify } from '../MarkdownLinkify';
import {
  CONFLICT_HEADER,
  IMPROBABLE_DAYS_HEADER,
  PATIENT_OVERLAP_WITH_DIFFERENT_PROVIDERS_HEADER,
  PAYOR_NO_SAME_DAY_HEADER,
} from '../constants';
import { GetSchedulingAlertsQueryData, SchedulingAlertObject } from './useGetSchedulingAlerts';

// should match exactly with SchedulingAlertType on back end
type SchedulingAlertType =
  | 'kpnc_same_day'
  | 'payor_no_same_day'
  | 'patient_overlap_with_different_providers'
  | 'conflict'
  | 'improbable_days';

/* eslint-disable camelcase */
const ALERT_HEADERS: Record<SchedulingAlertType, string> = {
  kpnc_same_day: PAYOR_NO_SAME_DAY_HEADER,
  payor_no_same_day: PAYOR_NO_SAME_DAY_HEADER,
  patient_overlap_with_different_providers: PATIENT_OVERLAP_WITH_DIFFERENT_PROVIDERS_HEADER,
  conflict: CONFLICT_HEADER,
  improbable_days: IMPROBABLE_DAYS_HEADER,
};
/* eslint-enable camelcase */

interface SchedulingAlertProps {
  schedulingAlert: SchedulingAlertObject;
}

function SchedulingAlert({ schedulingAlert }: SchedulingAlertProps): JSX.Element {
  return (
    <Alert
      className='fs-exclude'
      use={schedulingAlert?.alertLevel as AlertBannerUse}
      heading={ALERT_HEADERS[schedulingAlert?.alertType as SchedulingAlertType] ?? null}
      data-testid='scheduling_alert'
    >
      <MarkdownLinkify markdown={schedulingAlert?.alertMessage ?? ''} linkUse={LinkUse.Neutral} />
    </Alert>
  );
}

interface SchedulingAlertsProps {
  alertsData: GetSchedulingAlertsQueryData;
  className?: string;
}

export function SchedulingAlerts({ alertsData, className }: SchedulingAlertsProps): JSX.Element | null {
  const alerts = alertsData?.schedulingAlerts || [];
  if (!alerts.length) {
    return null;
  }

  return (
    <div className={twMerge(className, 'grid gap-y-4')}>
      {alerts.map((alert) => (
        <SchedulingAlert key={alert?.alertType} schedulingAlert={alert as SchedulingAlertObject} />
      ))}
    </div>
  );
}
